import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';

import { Dropdown as UnstyledDropdown } from './dropdown';
import { coral, grey25 } from '@zocdoc/branding/colors';
import ErrorMessage from '../ErrorMessage';
import { breakpoint, clearfix } from '../grid';
import { Arrow as UnstyledArrow } from '../icons';
import { sharpSansSemibold } from '../typography';

export class DropdownWrapper extends PureComponent {
    static propTypes = {
        groupBy: PropTypes.string,
        inline: PropTypes.bool,
        invalidMessage: PropTypes.string,
        onChange: PropTypes.func,
        options: PropTypes.array,
        placeholder: PropTypes.string,
        selected: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
    };

    static defaultProps = {
        groupBy: null,
        inline: false,
        onChange: noop,
        options: [],
        selected: null,
    };

    render() {
        const Component = this.props.inline ? FauxInlineSelect : FauxSelect;

        return (
            <Wrapper
                className={this.props.className}
                style={this.props.style}
            >
                <Component>
                    {this.renderDropdown()}
                    {this.renderSelected()}
                </Component>

                {this.renderInvalidErrorMessage()}
            </Wrapper>
        );
    }

    renderSelected() {
        const {
            selected,
            placeholder,
            options,
            invalidMessage,
        } = this.props;

        const selectedText = selected ? this.findSelected(selected, options, placeholder) : undefined;
        const Component = invalidMessage ? ErroredSelectGroup : SelectGroup;

        return (
            <Component inline={this.props.inline}>
                <SelectedGroup>
                    <SelectedText data-test="selected-text">{selectedText || placeholder}</SelectedText>
                    <Arrow
                        color="currentColor"
                        direction="down"
                    />
                </SelectedGroup>
            </Component>
        );
    }

    renderDropdown() {
        return <Dropdown {...this.props} />;
    }

    renderInvalidErrorMessage() {
        if (this.props.invalidMessage) {
            return (
                <div>
                    <ErrorMessage>
                        {this.props.invalidMessage}
                    </ErrorMessage>
                </div>
            );
        }
    }

    findSelected(selected, options, placeholder) {
        const flattenedList = options.reduce((a, b) =>
            ((b.options)
                ? a.concat(b.options)
                : a.concat(b)),
        []);

        // the double == operator will allow users to either have string or numerical ids!
        const selectedOption = flattenedList.find(x => x.id == selected); //eslint-disable-line

        return selectedOption ? selectedOption.name : placeholder;
    }
}

const Arrow = styled(UnstyledArrow)`
    flex-grow: 0;
    flex-shrink: 0;
    font-size: 0.45em;
    margin-right: 19px;
    pointer-events: none;
`;

const BaseSelectGroup = styled.div`
    box-sizing: border-box;
    border-style: solid;
    cursor: pointer;
    width: 100%;
`;

export const SelectGroup = styled(BaseSelectGroup)`
    border-color: ${grey25};
    border-width: ${p => p.inline ? '0' : '1px'};
`;

export const ErroredSelectGroup = styled(BaseSelectGroup)`
    border-color: ${coral};
    border-width: ${p => p.inline ? '0' : '1px'};
`;

const Dropdown = styled(UnstyledDropdown)`
    border: none;
    cursor: pointer;
    font-size: 15px;
    height: 100%;
    max-height: 56px;
    opacity: 0;
    outline: 0;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;

    &:focus + ${SelectGroup} {
        outline: #5098FF solid 3px;
    }
`;

const SelectedGroup = styled.div`
    ${clearfix}
    align-items: center;
    display: flex;
    justify-content: flex-start;
    height: 46px;
    max-width: 100%;
`;

const SelectedText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FauxSelectStyles = css`
    background-color: #FFF;
    color: currentColor;
    height: inherit;
    line-height: 35px;
    position: relative;

    ${SelectedText} {
        box-sizing: content-box;
        display: block;
        flex-grow: 1;
        flex-shrink: 1;
        overflow: hidden;
        padding: 0 16px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    ${Arrow} {
        transform: translateY(1px);
    }
`;

const FauxSelect = styled.div`
    ${FauxSelectStyles}
`;

const FauxInlineSelect = styled.div`
    color: currentColor;
    display: inline-block;
    max-width: 100%;
    position: relative;
    width: auto;

    ${SelectedGroup} {
        align-items: baseline;
        height: auto;
        padding: 0;
    }

    ${Arrow} {
        margin: 0 0 0 1em;
        transform: translateY(-0.5px); // optical adjustment
    }
`;

const Wrapper = styled.div`
    ${sharpSansSemibold(13, 24)}

    ${breakpoint.medium} {
        ${sharpSansSemibold(15, 24)}
    }
`;

export const primitives = {
    Arrow,
    BaseSelectGroup,
    Dropdown,
    ErroredSelectGroup,
    FauxInlineSelect,
    FauxSelect,
    FauxSelectStyles,
    SelectedGroup,
    SelectedText,
    SelectGroup,
    Wrapper,
};
